import React, { useEffect, useState } from "react";
import { Card, CardBody, Row, Col, Badge } from "reactstrap";
import ApiService from "../../services/apiService"
import { Icon, LucideMail, LucideGlobe,LucideLocate,LucideHome,LucidePhone,LucideSettings2 } from "lucide-react";
// ─── Types ────────────────────────────────────────────────────────────────────

interface UserDetail {
  id: string;
  fullName: string;
  email: string;
  mobileNumber: string;
  createdDateTime: string;
  houseNo: string;
  streetLandmark: string;
  area: string;
  village: string;
  city: string;
  district: string;
  state: string;
  pincode: string;
  country: string;
  profileImageUrl: string;
  mailStatus: string;
  role: string;
  enabled: number;
  deleteDateTime: string | null;
  landSizeUnitId: 1;
}

interface InstrumentCounts {
  pump: number;
  valve: number;
  sensor: number;
  farms: number;
}

// ─── Sample Data ──────────────────────────────────────────────────────────────

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getInitials(name: string): string {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

// ─── Sub-components ───────────────────────────────────────────────────────────

interface DetailCellProps {
  icon: React.ElementType;
  label: string;
  value: React.ReactNode;
  sub?: string;
}

const formatFields = (...fields: (string | null | undefined)[]) => {
  const result = fields
    .filter(v => v?.trim())
    .join(", ");

  return result || "Not Available";
};

const DetailCell: React.FC<DetailCellProps> = ({ icon:Icon, label, value, sub }) => (
  <div  className="user-detail-cell">
    <div  className="user-detail-cell-label">
       <Icon size={15}/>
      {label}
    </div>
    <div  className="user-detail-cell-value">{value && value?.toString().length > 0 ? value : 'Not Available'}</div>
    {sub && <div  className="user-detail-cell-sub">{sub}</div>}
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────

interface UserDetailCardProps {
  user?: UserDetail;
}
interface UserDetailCardProps {
  userId?: string | number;
}

const UserDetailCard: React.FC<UserDetailCardProps> = ({ userId }) => {
  const [user, setUser] = useState<UserDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [instrumentCounts, setInstrumentCounts] =
    useState<InstrumentCounts | null>(null);

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    const getUserDetail = async () => {
      try {
        const res = await ApiService.axiosApi(`/users/getuserbyid/${userId}`, {
          method: "GET",
        });

        if (res.success) {
          setUser(res.data);
        } else {
          console.error(res.data);
        }
      } catch (e) {
        console.error("UserDetailCard :", e);
      } finally {
        setLoading(false);
      }
    };

    const getInstrumentCount = async () => {
      try {
        const res = await ApiService.axiosApi(
          `/users/instrument-count/${userId}`,
          {
            method: "GET",
          },
        );

        if (res.success) {
          setInstrumentCounts(res.data);
        } else {
          console.error(res.data);
        }
      } catch (e) {
        console.error("UserDetailCard :", e);
      } finally {
        setLoading(false);
      }
    };

    getInstrumentCount();
    getUserDetail();                                                                                                       
    
  }, [userId]);

  if (loading)
    return <p style={{ padding: 16, color: "#6c757d" }}>Loading...</p>;
  if (!user)
    return <p style={{ padding: 16, color: "#6c757d" }}>Select a user.</p>;

  const initials = getInitials(user.fullName);

  return (
    <div  className="user-detail-wrapper">
      <p className="user-detail-section-label">USER DETAILS</p>

      <Card className=" rounded-10 overflow-hidden">
        <CardBody style={{ padding: 0 }}>
          <Row style={{ margin: 0 }}>
            {/* ── Left: Avatar column ── */}
            <Col xs="auto"  className="user-detail-avatar-col">
              <div className="user-detail-avatar">{user.profileImageUrl ?
               (<img src={user.profileImageUrl} alt="user image"
                style={{objectFit:'cover',
                width:'100%',
                height:'100%',
                borderRadius:'50%'}}/>)
                :initials}</div>
              <div  className="user-detail-name">{user.fullName}</div>
              <div  className="user-detail-role">{user.role}</div>
              <div  className="user-detail-status-pill">
                <span  className="user-detail-status-dot"/>
                {user.enabled === 1 ? "Active" : "Inactive"}
              </div>
            </Col>

            {/* ── Right: Detail grid ── */}
            <Col style={{ padding: 0, borderLeft: "0.5px solid #dee2e6" }}>
              <Row style={{ margin: 0, height: "100%" }}>
                <Col xs={4}  className="user-detail-first-row">
                  <DetailCell
                    icon={LucidePhone}
                    label="Phone"
                    value={formatFields(user.mobileNumber)}
                    sub="Primary contact"
                  />
                </Col>
                <Col
                  xs={4}
                  className="user-detail-first-row user-detail-cell-border-x"

                >
                  <DetailCell
                    icon={LucideMail}
                    label="Email"
                    value={<span style={{ color: PRIMARY }}> { formatFields(user.email) }</span>}
                    sub={formatFields(user.mailStatus) } // ← "Pending" / "Verified"
                  />
                </Col>
                <Col xs={4}  className="user-detail-first-row">
                  <DetailCell
                    icon={LucideLocate}
                    label="Location"
                    value={ formatFields(user.city,user.district)}
                    sub={ formatFields(user.state,user.pincode)}
                  />
                </Col>

                {/* Row 2 */}
                <Col xs={4}  className="user-detail-second-row">
                  <DetailCell
                    icon={LucideSettings2}
                    label="Instruments"
                    value={
                      <span style={{ color: "#185FA5" }}>
                        {(instrumentCounts?.pump || 0) +
                          (instrumentCounts?.sensor || 0) +
                          (instrumentCounts?.valve || 0)}{" "}
                        instruments
                      </span>
                    }
                  />
                </Col>
                <Col
                  xs={4}
                  className="user-detail-second-row user-detail-cell-border-x"
                  
                >
                  <DetailCell
                    icon={LucideHome}
                    label="Address"
                    value={formatFields(user.houseNo , user.streetLandmark)}
                    sub={formatFields(user.area)}
                  />
                </Col>
                <Col xs={4}  className="user-detail-second-row">
                  <DetailCell
                    icon={LucideGlobe}
                    label="Country"
                    value={formatFields(user.country)}
                    sub={formatFields(user.role)}
                  />
                </Col>
               
              </Row>
            </Col>
          </Row>
        </CardBody>
      </Card>
    </div>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const PRIMARY = "#1e563d";
//const PRIMARY_LIGHT = "#e8f2ed";
const PRIMARY_MUTED = "rgba(255,255,255,0.65)";

const styles: Record<string, React.CSSProperties> = {
  wrapper: {
    marginBottom: "1.5rem",
  },
  sectionLabel: {
    fontSize: 14,
    fontWeight: 500,
    color: "#6c757d",
    letterSpacing: "0.06em",
    marginBottom: 8,
  },
  card: {
    border: "0.5px solid #dee2e6",
    borderRadius: 12,
    overflow: "hidden",
    boxShadow: "none",
  },

  // Avatar column
  avatarCol: {
    background: PRIMARY,
    padding: "24px 28px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    minWidth: 148,
  },
  avatar: {
    width: 60,
    height: 60,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.18)",
    border: "2px solid rgba(255,255,255,0.35)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 22,
    fontWeight: 500,
    color: "#fff",
  },
  userName: {
    fontSize: 13,
    fontWeight: 500,
    color: "#fff",
    textAlign: "center",
  },
  userRole: {
    fontSize: 11,
    color: PRIMARY_MUTED,
    textAlign: "center",
  },
  statusPill: {
    background: "rgba(255,255,255,0.15)",
    border: "0.5px solid rgba(255,255,255,0.25)",
    borderRadius: 10,
    padding: "3px 10px",
    fontSize: 10,
    color: "#fff",
    display: "flex",
    alignItems: "center",
    gap: 5,
    marginTop: 2,
  },
  statusDot: {
    width: 6,
    height: 6,
    borderRadius: "50%",
    background: "#6ee7a0",
    flexShrink: 0,
  },

  // Detail grid rows
  firstRow: {
    padding: 0,
    borderBottom: "0.5px solid #dee2e6",
  },
  secondRow: {
    padding: 0,
  },
  cellBorderX: {
    borderLeft: "0.5px solidrgb(205, 209, 212)",
    borderRight: "0.5px solidrgb(205, 209, 212)",
  },

  // Detail cell
  detailCell: {
    padding: "14px 16px",
    height: "100%",
  },
  cellLabel: {
    fontSize: 16,
    color: "#6c757d",
    marginBottom: 5,
    display: "flex",
    alignItems: "center",
    gap: 5,
  },
  cellIcon: {
    fontSize: 13,
  },
  cellValue: {
    fontSize: 14,
    fontWeight: 500,
    color: "#212529",
  },
  cellSub: {
    fontSize: 12,
    color: "#6c757d",
    marginTop: 3,
  },
};

export default UserDetailCard;
